// Контракт справочника тем для формы выдачи домашки:
// `GET /v1/homework/generator-topics` (4.1 docs/homework-phase2-design.md).
//
// ⚠️ Зачем ручка, если словарь навыков лежит в корневом экспорте и фронту
// доступен. Словарь знает НАЗВАНИЯ, но не знает ПОКРЫТИЯ, а реестр, который
// знает, лежит за барьером `@edu/shared/exercises/oge` вместе с правильными
// ответами, и бандл у web один на обе роли. Поэтому справочник приезжает
// данными, и в этих данных только показываемая половина.
//
// ⚠️ Названия тем тоже приезжают с сервера, а не берутся из бандла, и это уже
// не про барьер, а про размер стартового чанка: форма выдачи НЕ ленивая, а
// `SKILLS` плюс `SECTIONS` это несколько килобайт данных, нужных одному экрану
// из десяти. Экран карты покрытия сделан ленивым ровно по этой причине.
//
// ⚠️ Форма отличается от плоского списка, записанного в 7.7
// docs/homework-design.md, и это правка контракта, а не описка: плоский
// `difficulties: number[]` не отвечает на вопрос «сколько генераторов у ЭТОГО
// уровня», без которого не собрать ни предпросмотр состава, ни подпись
// «сложного по этой теме пока нет».
//
// Сама сборка справочника по реестру живёт за барьером, в
// `oge/generators/topics.ts`: она принимает реестр на вход.

import { z } from "zod";
import { OgeGeneratorInfo } from "./coverage.ts";

/**
 * Генератор в справочнике формы: показываемая половина покрытия плюс форма
 * ответа.
 *
 * ⚠️ `answerKind` добавлен шагом Б4 и нужен ровно предпросмотру состава.
 * Причина: у темы «Арифметический квадратный корень» среднего уровня два
 * генератора, и один спрашивает вводом числа, а другой выбором из вариантов.
 * Домашка из четырёх заданий по этой теме уже сегодня приезжает ученику двумя
 * заданиями одной формы и двумя другой, преподаватель об этом ниоткуда не
 * узнаёт, а состав фиксируется в момент создания и не правится. Это тот же
 * довод, по которому 5.6 запрещает молчать про «5 заданий из 1 генератора».
 *
 * Секрета в поле нет: форму ответа ученик видит в самом задании, а какой ответ
 * верный, из неё не следует. В покрытие (`./coverage.ts`) поле не добавлено:
 * там другой потребитель и другой вопрос, и расширять контракт карты ради
 * формы значит связать два экрана, которые 4.1 разводит намеренно.
 */
export const OgeTopicGenerator = OgeGeneratorInfo.extend({
  answerKind: z.enum(["choice", "number"]),
});
export type OgeTopicGenerator = z.infer<typeof OgeTopicGenerator>;

export const OgeTopicLevel = z.object({
  /** 1 лёгкий, 2 средний, 3 сложный. Словом называет `./difficulty.ts`. */
  difficulty: z.number().int().min(1).max(3),
  /**
   * Потолок количества заданий по этой теме и уровню.
   *
   * ⚠️ Это ограничитель, а не справка. Считается от размера пространства задач
   * генераторов уровня, а не от потолка домашки: у `percent-of` различных задач
   * всего 48, и «дай 50 по процентам» при включённой дедупликации физически
   * невыполнимо. Форма ограничивает поле ввода этим числом, то есть до отказа
   * `generator_unavailable` доходить не должна.
   */
  maxCount: z.number().int().min(1),
  /**
   * ⚠️ Массив, а не число, и порядок в нём это ПОРЯДОК РЕЕСТРА, часть
   * контракта. На нём держится честность предпросмотра состава: по тому же
   * порядку сервер раскладывает задания по кругу между генераторами темы.
   * Отсортировать его «для красоты» на любой из двух сторон значит показать
   * преподавателю не тот состав, который приедет ученику.
   */
  generators: z.array(OgeTopicGenerator).min(1),
});
export type OgeTopicLevel = z.infer<typeof OgeTopicLevel>;

export const OgeTopic = z.object({
  /**
   * Слаг навыка. Свободная строка, а не юнион, по той же причине, что в
   * покрытии: старый бандл обязан переварить ответ с новым навыком, а не упасть
   * на разборе.
   */
  skill: z.string().min(1),
  name: z.string().min(1),
  section: z.string().min(1),
  /** Название раздела: чтобы клиенту не понадобился словарь `SECTIONS`. */
  sectionName: z.string().min(1),
  /** Класс программы: «это за 5 класс» читается иначе, чем «за 9». */
  grade: z.string().min(1),
  /** Уровни по возрастанию. Пустым не бывает: тема без генераторов не едет. */
  levels: z.array(OgeTopicLevel).min(1),
});
export type OgeTopic = z.infer<typeof OgeTopic>;

export const OgeTopicsResponse = z.object({
  /** Предмет, сейчас всегда `oge-math`. Обязателен по той же причине, что в покрытии. */
  subject: z.string().min(1),
  /**
   * ⚠️ Только темы, у которых есть хотя бы один генератор. Тема без
   * генераторов в форме не предлагается вовсе: предлагать её значит обещать
   * отказ после нажатия.
   */
  topics: z.array(OgeTopic),
});
export type OgeTopicsResponse = z.infer<typeof OgeTopicsResponse>;
